import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { BellRing } from 'lucide-react';
import {
  advanceLeadStage,
  bookAppointment,
  fetchDaySlots,
  fetchLeadsWorkspace,
  fetchMyWorkActions,
  transitionWorkAction,
  type LeadStageTarget,
} from '@/dashboard/api';
import {
  DaySlotPicker,
  type DaySlotsState,
} from '@/dashboard/appointments/DaySlotPicker';
import { useDashboardMeContext } from '@/dashboard/me-context';
import CallcenterCommandCenter from '@/dashboard/lead-cockpit/CallcenterCommandCenter';
import type { Lead360Input } from '@/dashboard/owner/lead-360-view';
import {
  appointmentStatusLabel,
  compactSourceSummary,
  formatTimestampIst,
  hasResolvedSourceSummary,
} from '@/dashboard/owner/Lead360Panel';
import {
  formatRelativeAge,
} from '@/dashboard/workboard/workboard-view';
import {
  removeFollowupRow,
  type FollowupRow,
  type FollowupTransition,
} from '@/walkin/WalkinFollowupsPanel';
import {
  activeAppointmentCard,
  BOOKING_CEILING_NOTE,
  buildLeadWorklist,
  bookLeadAppointment,
  CALL_CENTER_REDUCED_NOTE,
  CALL_CENTER_STAGE_TARGETS,
  ENRICHMENT_UNAVAILABLE_NOTE,
  findWorkspaceRecord,
  FRONT_HALF_STAGE_TARGETS,
  LEAD_COCKPIT_CEILING_NOTE,
  leadCountLabel,
  leadFollowups,
  moveLeadStage,
  type BookAppointmentDeps,
  type CockpitFeedback,
  type LeadWorklistEntry,
  type MoveStageDeps,
  type StageTargetOption,
} from '@/dashboard/lead-cockpit/lead-cockpit-view';

/**
 * AdoraCare - Lead-to-Appointment cockpit (LEAD-TO-APPOINTMENT-COCKPIT-V1).
 *
 * ONE operating surface for the agent/COC's lead-to-appointment loop, replacing
 * app-switching (CRM + WhatsApp + chat + booking tool) with a single screen:
 * pick a lead from your open-follow-up worklist -> see its context + real source
 * + appointment status -> book an appointment / move the lead stage / clear the
 * follow-up in place -> link out to the appointment workbench for the detailed
 * lifecycle. It is frontend-composed: every affordance is an EXISTING client fn
 * (fetchMyWorkActions, fetchLeadsWorkspace, bookAppointment, advanceLeadStage,
 * transitionWorkAction) - no new endpoint, no migration, no flag.
 *
 * HONEST CEILING (see lead-cockpit-view.ts): the booking control is the SHARED
 * server-computed slot grid (DaySlotPicker, LEAD-APPOINTMENT-CRM-COHERENCE-V1
 * SCOPE 2 - the same picker the Lead-360 workbench and the journey stepper use);
 * there is NO hour/minute picker and NO doctor picker (the create route always
 * leaves doctor_id null). The full appointment lifecycle already lives in the
 * appointment workbench, so this cockpit LINKS there rather than duplicating it.
 * The literal pipeline stage is not projected onto the lead read, so this
 * cockpit shows the appointment status the read DOES carry and never invents a
 * "current stage". These deferrals + the no-outbound-send fact are stated once
 * in LEAD_COCKPIT_CEILING_NOTE / BOOKING_CEILING_NOTE.
 *
 * COMPLIANCE: agent/COC are non-clinical - this cockpit renders NO clinical
 * field (clinical_note / findings_diagnosis / chief_complaint), only the carved
 * lead name + administrative source + administrative appointment status.
 *
 * Tested via react-dom/server (no jsdom): the pure LeadCockpitContent + the pure
 * helpers in lead-cockpit-view.ts are exercised with controlled props; the
 * default export wires the real client fns in effects that SSR does not run (so
 * the static render is the loading state).
 */

// --- Controlled state shapes (fully props-driven for SSR testability) -------

export type WorklistState =
  | { kind: 'loading' }
  | { kind: 'ready'; rows: FollowupRow[] }
  | { kind: 'error'; message: string };

export type EnrichmentState =
  | { kind: 'off' }
  | { kind: 'loading' }
  | { kind: 'ready'; records: Lead360Input[] }
  | { kind: 'error' };

export interface BookingForm {
  readonly date: string;
  /** The chosen slot's UTC instant (from the server slot grid), or ''. */
  readonly slotIso: string;
}

export const EMPTY_BOOKING_FORM: BookingForm = { date: '', slotIso: '' };

/** The picker's slot-grid state (server-computed truth). Re-exported from the
 *  shared DaySlotPicker module (LEAD-APPOINTMENT-CRM-COHERENCE-V1 SCOPE 2) so the
 *  cockpit, the Lead-360 workbench, and the journey stepper share ONE type. */
export type { DaySlotsState };

// --- Shared banners ---------------------------------------------------------

const SANDBOX_NOTE =
  'Sandbox - synthetic test data only. AdoraCare owns this lead record; it never flows over WhatsApp.';

function SandboxBanner(): JSX.Element {
  return (
    <div className="clinic-workbench__banner" role="note" data-testid="lead-cockpit-banner">
      {SANDBOX_NOTE}
    </div>
  );
}

function FeedbackBanner({ feedback }: { feedback: CockpitFeedback | null }): JSX.Element | null {
  if (feedback === null) return null;
  return (
    <div
      className={
        feedback.kind === 'success'
          ? 'workboard__feedback workboard__feedback--success'
          : 'workboard__feedback workboard__feedback--error'
      }
      role="status"
      aria-live="polite"
      data-testid="lead-cockpit-feedback"
    >
      <div className="clinic-workbench__feedback-lines">
        {feedback.lines.map((line, i) => (
          <span key={String(i)}>{line}</span>
        ))}
      </div>
    </div>
  );
}

// --- 1. Worklist (from the always-on work-actions feed) ---------------------

export function WorklistPanel({
  state,
  selectedLeadId,
  nowMs,
  onSelect,
  onRetry,
}: {
  state: WorklistState;
  selectedLeadId: number | null;
  nowMs: number;
  onSelect: (leadId: number) => void;
  onRetry: () => void;
}): JSX.Element {
  const entries: LeadWorklistEntry[] =
    state.kind === 'ready' ? buildLeadWorklist(state.rows) : [];
  return (
    <section className="clinic-table lead-cockpit__worklist" data-testid="lead-cockpit-worklist">
      <div className="lead-cockpit__worklist-head">
        <h2 className="clinic-table__title">Your leads</h2>
        {state.kind === 'ready' ? (
          <span className="lead-cockpit__count" data-testid="lead-cockpit-count">
            {leadCountLabel(entries.length)}
          </span>
        ) : null}
      </div>
      <p className="clinic-workbench__hint">
        Leads with an open follow-up assigned to you. Pick one to see its context
        and book or advance it.
      </p>

      {state.kind === 'loading' ? (
        <p className="followups-empty__sub" role="status" data-testid="lead-cockpit-worklist-loading">
          Loading your leads...
        </p>
      ) : state.kind === 'error' ? (
        <div data-testid="lead-cockpit-worklist-error">
          <p className="followups-empty__sub" role="status">
            Your leads are unavailable right now.
          </p>
          <button type="button" className="ac-btn ac-btn--secondary ac-btn--sm" onClick={onRetry}>
            Retry
          </button>
        </div>
      ) : entries.length === 0 ? (
        <div className="followups-empty" role="status" data-testid="lead-cockpit-worklist-empty">
          <BellRing className="followups-empty__icon" size={20} aria-hidden="true" />
          <p className="followups-empty__title">No leads with an open follow-up assigned to you.</p>
        </div>
      ) : (
        <ul className="lead-cockpit__lead-list" data-testid="lead-cockpit-lead-list">
          {entries.map((e) => (
            <li key={String(e.leadId)}>
              <button
                type="button"
                className={
                  selectedLeadId === e.leadId
                    ? 'lead-cockpit__lead-row lead-cockpit__lead-row--active'
                    : 'lead-cockpit__lead-row'
                }
                aria-pressed={selectedLeadId === e.leadId}
                data-testid={`lead-cockpit-lead-${e.leadId}`}
                onClick={() => onSelect(e.leadId)}
              >
                <span className="lead-cockpit__lead-name">{e.name || 'Unknown patient'}</span>
                <span className="lead-cockpit__lead-id">#{e.leadId}</span>
                <span className="lead-cockpit__lead-meta">
                  {e.followupCount} open &middot; {formatRelativeAge(e.oldestCreatedAt, nowMs)}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

// --- 2. Context card --------------------------------------------------------

export function ContextCard({
  leadId,
  name,
  record,
  enrichment,
}: {
  leadId: number;
  name: string;
  record: Lead360Input | null;
  enrichment: EnrichmentState;
}): JSX.Element {
  // Real source ONLY from the workspace record (never the feed's fixed 'Call').
  const sourceKnown = record !== null && hasResolvedSourceSummary(record);
  return (
    <section className="clinic-table cockpit-context" data-testid="lead-cockpit-context">
      <div className="cockpit-context__head">
        <h2 className="clinic-table__title">{name || 'Unknown patient'}</h2>
        <span className="cockpit-pill cockpit-pill--muted">Lead #{leadId}</span>
      </div>
      <dl className="cockpit-context__meta">
        <div className="cockpit-context__meta-row">
          <dt>Source</dt>
          <dd data-testid="lead-cockpit-source">
            {sourceKnown ? (
              compactSourceSummary(record as Lead360Input)
            ) : record !== null ? (
              'Source not captured'
            ) : (
              <span className="clinic-workbench__muted">Not loaded</span>
            )}
          </dd>
        </div>
        {record?.clinicLabel ? (
          <div className="cockpit-context__meta-row">
            <dt>Clinic</dt>
            <dd>{record.clinicLabel}</dd>
          </div>
        ) : null}
      </dl>
      {enrichment.kind === 'off' || enrichment.kind === 'error' ? (
        <p className="clinic-workbench__note" role="note" data-testid="lead-cockpit-enrichment-note">
          {ENRICHMENT_UNAVAILABLE_NOTE}
        </p>
      ) : null}
    </section>
  );
}

// --- 3. Appointment status --------------------------------------------------

export function AppointmentStatusCard({
  record,
  enrichment,
}: {
  record: Lead360Input | null;
  enrichment: EnrichmentState;
}): JSX.Element {
  const section = record?.appointmentSection;
  return (
    <section className="clinic-table lead-cockpit__appt" data-testid="lead-cockpit-appt-status">
      <h2 className="clinic-table__title">Appointment status</h2>
      {record === null ? (
        <p className="clinic-workbench__hint" data-testid="lead-cockpit-appt-not-loaded">
          {enrichment.kind === 'off' || enrichment.kind === 'error'
            ? 'Appointment status is not loaded - enable the Leads directory to see it.'
            : 'No appointment details for this lead.'}
        </p>
      ) : section === undefined || !section.hasAppointments ? (
        <p className="clinic-workbench__hint" data-testid="lead-cockpit-appt-none">
          {section?.emptyStateLabel ?? 'No appointment yet.'}
        </p>
      ) : (
        <ul className="lead-cockpit__appt-list" data-testid="lead-cockpit-appt-list">
          {section.cards.map((c) => (
            <li key={String(c.appointmentId)} className="lead-cockpit__appt-row">
              <span className="cockpit-pill" data-testid={`lead-cockpit-appt-status-${c.appointmentId}`}>
                {appointmentStatusLabel(c)}
              </span>
              <span className="lead-cockpit__appt-time">{formatTimestampIst(c.appointmentTime)}</span>
              <span className="lead-cockpit__appt-doctor">
                {c.doctorAssigned ? 'Doctor assigned' : 'No doctor assigned'}
              </span>
              {c.category ? <span className="lead-cockpit__appt-cat">{c.category}</span> : null}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

// --- 4a. Booking (honest ceiling: a chosen time, no slots / no doctor) ------

export function BookingPanel({
  leadId,
  record,
  bookingEnabled,
  directoryEnabled,
  form,
  slots,
  pending,
  onFormChange,
  onBook,
}: {
  leadId: number;
  record: Lead360Input | null;
  bookingEnabled: boolean;
  directoryEnabled: boolean;
  form: BookingForm;
  /** Server slot grid for the picked date (STITCH-2 SCOPE 8). */
  slots: DaySlotsState;
  pending: boolean;
  onFormChange: (form: BookingForm) => void;
  onBook: () => void;
}): JSX.Element {
  const active = activeAppointmentCard(record);
  const preview = form.slotIso !== '' ? form.slotIso : null;

  return (
    <section className="clinic-table lead-cockpit__book" data-testid="lead-cockpit-book">
      <h2 className="clinic-table__title">Book an appointment</h2>

      {active !== null ? (
        // The lead already has an active appointment - a second booking would 409
        // (active_appointment_exists), so offer the workbench link, not a picker.
        <p className="clinic-workbench__hint" data-testid="lead-cockpit-book-active">
          This lead already has a {appointmentStatusLabel(active).toLowerCase()} appointment.
          {directoryEnabled ? (
            <>
              {' '}
              <Link to={`/dashboard/leads/${encodeURIComponent(String(leadId))}`}>
                Manage it in the appointment workbench
              </Link>
              .
            </>
          ) : (
            ' Manage it in the appointment workbench.'
          )}
        </p>
      ) : !bookingEnabled ? (
        <p className="clinic-workbench__hint" data-testid="lead-cockpit-book-disabled">
          Appointment booking is not enabled here yet.
        </p>
      ) : (
        <>
          {/* LEAD-APPOINTMENT-CRM-COHERENCE-V1 (SCOPE 2): the ONE shared
              server-computed slot grid (DaySlotPicker) - identical to the
              Lead-360 workbench and the journey stepper. The write route enforces
              the same booking-rules config server-side (one rule source). */}
          <DaySlotPicker
            date={form.date}
            onDateChange={(date) => onFormChange({ date, slotIso: '' })}
            slots={slots}
            selectedIso={form.slotIso}
            onSelectIso={(slotIso) => onFormChange({ ...form, slotIso })}
            disabled={pending}
            idPrefix="lead-cockpit"
          />

          <div className="clinic-workbench__actions">
            <button
              type="button"
              className="ac-btn ac-btn--primary ac-btn--sm"
              data-testid="lead-cockpit-book-submit"
              disabled={pending || preview === null}
              onClick={onBook}
            >
              Book appointment
            </button>
          </div>

          <p className="clinic-workbench__note" role="note" data-testid="lead-cockpit-book-ceiling">
            {BOOKING_CEILING_NOTE}
          </p>
        </>
      )}
    </section>
  );
}

// --- 4b. Stage advance (front-half targets, reused Workboard ladder) --------

export function StageActions({
  pending,
  onAdvanceStage,
  targets = FRONT_HALF_STAGE_TARGETS,
}: {
  pending: boolean;
  onAdvanceStage: (to: LeadStageTarget) => void;
  /**
   * The stage-advance targets to offer. Defaults to the full front-half ladder
   * (byte-identical for every existing caller). V2-P0d passes a reduced set
   * (CALL_CENTER_STAGE_TARGETS = Qualify only) for the call_center_agent, whose
   * config-granted authority covers `qualified` alone -- the other targets 401.
   */
  targets?: readonly StageTargetOption[];
}): JSX.Element {
  return (
    <section className="clinic-table lead-cockpit__stage" data-testid="lead-cockpit-stage">
      <h2 className="clinic-table__title">Move lead stage</h2>
      <p className="clinic-workbench__hint">
        Advance this lead along the front-half pipeline. Only forward moves the
        backend allows are offered.
      </p>
      <div className="clinic-workbench__actions" role="group" aria-label="Advance stage">
        {targets.map((t) => (
          <button
            key={t.to}
            type="button"
            className="ac-btn ac-btn--secondary ac-btn--sm"
            data-testid={`lead-cockpit-stage-${t.to}`}
            disabled={pending}
            onClick={() => onAdvanceStage(t.to)}
          >
            {t.label}
          </button>
        ))}
      </div>
    </section>
  );
}

// --- 4c. Follow-up actions (reuse #183's transition mechanism) --------------

export function LeadFollowupActions({
  rows,
  busyId,
  onTransition,
}: {
  rows: FollowupRow[];
  busyId: number | null;
  onTransition: (id: number, status: FollowupTransition) => void;
}): JSX.Element {
  return (
    <section className="clinic-table lead-cockpit__followups" data-testid="lead-cockpit-followups">
      <h2 className="clinic-table__title">Follow-ups for this lead</h2>
      {rows.length === 0 ? (
        <p className="clinic-workbench__hint" data-testid="lead-cockpit-followups-empty">
          No open follow-up for this lead.
        </p>
      ) : (
        <ul className="lead-cockpit__followup-list">
          {rows.map((r) => (
            <li key={String(r.id)} className="lead-cockpit__followup-row" data-testid={`lead-cockpit-followup-${r.id}`}>
              <span className="lead-cockpit__followup-reason">{r.reason || 'Follow-up'}</span>
              <div className="lead-cockpit__followup-actions">
                <button
                  type="button"
                  className="ac-btn ac-btn--primary ac-btn--sm"
                  data-testid={`lead-cockpit-followup-done-${r.id}`}
                  disabled={busyId === r.id}
                  onClick={() => onTransition(r.id, 'done')}
                >
                  Done
                </button>
                <button
                  type="button"
                  className="ac-btn ac-btn--secondary ac-btn--sm"
                  data-testid={`lead-cockpit-followup-dismiss-${r.id}`}
                  disabled={busyId === r.id}
                  onClick={() => onTransition(r.id, 'dismissed')}
                >
                  Dismiss
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

// --- 4d. Links into existing surfaces (reuse, never duplicate) --------------

export function CockpitLinks({
  leadId,
  directoryEnabled,
  detailOnly = false,
}: {
  leadId: number;
  directoryEnabled: boolean;
  /**
   * V2-P0d: when true render ONLY the canonical Lead-360 detail drill (the ONE
   * surface this package unblocks for the call_center_agent), omitting the
   * Workboard / clinical-workbench links that are not this role's home. Default
   * false -> the full link set (byte-identical for every existing caller). With
   * the workspace directory off there is no detail link to show, so the whole
   * section is suppressed rather than rendering an empty shell.
   */
  detailOnly?: boolean;
}): JSX.Element | null {
  const detailLink = directoryEnabled ? (
    <li>
      <Link to={`/dashboard/leads/${encodeURIComponent(String(leadId))}`} data-testid="lead-cockpit-link-detail">
        Appointment workbench (full lifecycle: arrival, check-in, reschedule, cancel, no-show, fulfilled)
      </Link>
    </li>
  ) : null;
  if (detailOnly && detailLink === null) return null;
  return (
    <section className="clinic-table lead-cockpit__links" data-testid="lead-cockpit-links">
      <h2 className="clinic-table__title">Open in a detailed surface</h2>
      <ul className="lead-cockpit__link-list">
        {detailLink}
        {detailOnly ? null : (
          <>
            <li>
              <Link to="/dashboard/workboard" data-testid="lead-cockpit-link-workboard">
                Workboard (all your assigned follow-ups)
              </Link>
            </li>
            <li>
              <Link to="/dashboard/clinical-workbench" data-testid="lead-cockpit-link-clinical">
                Follow-up queue (clinical workbench)
              </Link>
            </li>
          </>
        )}
      </ul>
    </section>
  );
}

// --- Pure presentational board (fully controlled; SSR-testable) -------------

export interface LeadCockpitContentProps {
  readonly worklist: WorklistState;
  readonly enrichment: EnrichmentState;
  readonly selectedLeadId: number | null;
  readonly bookingEnabled: boolean;
  readonly directoryEnabled: boolean;
  /**
   * OPERATOR-JOURNEY-STITCH-V1 (V5-P9): true ONLY for the call_center_agent
   * role, whose stage-advance (all 401 -- receptionist/coc-owned) + booking
   * (not in APPOINTMENT_CREATE_ROLES) + detail links (unreachable) are ALL
   * broken on this legacy cockpit. When true the panel HIDES those affordances
   * and shows CALL_CENTER_REDUCED_NOTE instead. Default false -> every other
   * cockpit role renders BYTE-IDENTICALLY (the package's operator-only bound).
   */
  readonly callCenterReduced?: boolean;
  readonly form: BookingForm;
  /** Server slot grid for the picked date (STITCH-2 SCOPE 8). */
  readonly slots: DaySlotsState;
  readonly pending: boolean;
  readonly busyFollowupId: number | null;
  readonly feedback: CockpitFeedback | null;
  readonly nowMs: number;
  readonly onSelectLead: (leadId: number) => void;
  readonly onFormChange: (form: BookingForm) => void;
  readonly onBook: () => void;
  readonly onAdvanceStage: (to: LeadStageTarget) => void;
  readonly onFollowupTransition: (id: number, status: FollowupTransition) => void;
  readonly onRetry: () => void;
}

/** Resolve the selected lead's display name (worklist entry -> record -> ''). */
function selectedLeadName(
  worklist: WorklistState,
  record: Lead360Input | null,
  leadId: number,
): string {
  if (worklist.kind === 'ready') {
    const entry = buildLeadWorklist(worklist.rows).find((e) => e.leadId === leadId);
    if (entry && entry.name) return entry.name;
  }
  return record?.leadName ?? '';
}

export function LeadCockpitContent({
  worklist,
  enrichment,
  selectedLeadId,
  bookingEnabled,
  directoryEnabled,
  callCenterReduced = false,
  form,
  slots,
  pending,
  busyFollowupId,
  feedback,
  nowMs,
  onSelectLead,
  onFormChange,
  onBook,
  onAdvanceStage,
  onFollowupTransition,
  onRetry,
}: LeadCockpitContentProps): JSX.Element {
  const records = enrichment.kind === 'ready' ? enrichment.records : null;
  const record =
    selectedLeadId === null ? null : findWorkspaceRecord(records, selectedLeadId);
  const followups =
    selectedLeadId !== null && worklist.kind === 'ready'
      ? leadFollowups(worklist.rows, selectedLeadId)
      : [];
  const name =
    selectedLeadId === null ? '' : selectedLeadName(worklist, record, selectedLeadId);

  return (
    <div className="dashboard clinic-workbench lead-cockpit">
      <div className="page-header">
        <h1>Lead to appointment</h1>
        <p className="page-header__sub">
          Book, advance, and follow up on a lead - one screen &middot; AdoraCare sandbox
        </p>
      </div>

      <SandboxBanner />

      <WorklistPanel
        state={worklist}
        selectedLeadId={selectedLeadId}
        nowMs={nowMs}
        onSelect={onSelectLead}
        onRetry={onRetry}
      />

      {selectedLeadId === null ? (
        <p className="clinic-workbench__hint" data-testid="lead-cockpit-no-selection">
          Select a lead above to open its cockpit.
        </p>
      ) : (
        <div className="lead-cockpit__panel" data-testid="lead-cockpit-panel">
          <ContextCard
            leadId={selectedLeadId}
            name={name}
            record={record}
            enrichment={enrichment}
          />
          <AppointmentStatusCard record={record} enrichment={enrichment} />
          {/*
            STAGE-ADVANCE-AUTHORITY-CONFIG-V1 (V2-P0d) UN-REDUCTION: the
            call_center_agent now has config-granted QUALIFY authority (Partha Q5)
            and a working Lead-360 drill (LEADS_WORKSPACE_ROLES widened), so we
            UN-hide exactly those: the Qualify-only StageActions shows + works,
            and the detail-only CockpitLinks below drills. What STAYS reduced is
            BOOKING -- the appointment create/slot flow (BookingPanel) is not in
            this role's grant (DELTA-6, carried by V2-P2) -- so it stays hidden
            behind the honest note. Every other cockpit role keeps the full
            control set byte-identically (callCenterReduced defaults false). The
            worklist + follow-up Done/Dismiss above/below stay live throughout.
          */}
          {callCenterReduced ? (
            <>
              <StageActions
                pending={pending}
                onAdvanceStage={onAdvanceStage}
                targets={CALL_CENTER_STAGE_TARGETS}
              />
              <p
                className="clinic-workbench__note lead-cockpit__reduced"
                role="note"
                data-testid="lead-cockpit-callcenter-reduced"
              >
                {CALL_CENTER_REDUCED_NOTE}
              </p>
            </>
          ) : (
            <>
              <BookingPanel
                leadId={selectedLeadId}
                record={record}
                bookingEnabled={bookingEnabled}
                directoryEnabled={directoryEnabled}
                form={form}
                slots={slots}
                pending={pending}
                onFormChange={onFormChange}
                onBook={onBook}
              />
              <StageActions pending={pending} onAdvanceStage={onAdvanceStage} />
            </>
          )}
          <LeadFollowupActions
            rows={followups}
            busyId={busyFollowupId}
            onTransition={onFollowupTransition}
          />
          <CockpitLinks
            leadId={selectedLeadId}
            directoryEnabled={directoryEnabled}
            detailOnly={callCenterReduced}
          />
          <FeedbackBanner feedback={feedback} />
        </div>
      )}

      <p
        className="clinic-workbench__note lead-cockpit__ceiling"
        role="note"
        data-testid="lead-cockpit-ceiling"
      >
        {LEAD_COCKPIT_CEILING_NOTE}
      </p>
    </div>
  );
}

// --- Stateful wirer (default export) ----------------------------------------

const BOOK_DEPS: BookAppointmentDeps = { bookAppointment };
const STAGE_DEPS: MoveStageDeps = { advanceLeadStage };

/**
 * CALLCENTER-QUEUE-QUALIFY-V1 (V2-P1): the lead-cockpit route dispatcher.
 * EVOLVES this surface IN PLACE -- when the /me `intakeQueueEnabled` signal is
 * present (flag on + intake audience), the route renders the operator command
 * center; otherwise the existing cockpit renders BYTE-IDENTICALLY. Branching in
 * a thin dispatcher (a single hook, then a component swap) keeps the Rules of
 * Hooks intact -- each child owns its own hooks.
 */
export default function LeadToAppointmentCockpit(): JSX.Element {
  const ctx = useDashboardMeContext();
  if (ctx?.me?.intakeQueueEnabled === true) {
    return <CallcenterCommandCenter />;
  }
  return <LegacyLeadCockpit />;
}

function LegacyLeadCockpit(): JSX.Element {
  const ctx = useDashboardMeContext();
  const me = ctx?.me ?? null;
  const directoryEnabled = me?.leadsWorkspaceEnabled === true;
  const bookingEnabled = me?.crmAppointmentWorkbenchEnabled === true;
  // OPERATOR-JOURNEY-STITCH-V1 (V5-P9): fail-close the affordances broken for
  // the call_center_agent role only (stage-advance 401s -- receptionist/coc-owned;
  // booking excluded -- DELTA-6; detail links unreachable). Scoped to this ONE
  // operator role so every other cockpit role stays byte-identical, per the
  // package's operator-only bound. A role literal here is deliberate and DELTA-
  // documented, not an ad-hoc gate: the broader capability grant is a founder
  // call (V2-P0d qualify authority + DELTA-6 booking).
  const callCenterReduced = me?.role === 'call_center_agent';

  const [worklist, setWorklist] = useState<WorklistState>({ kind: 'loading' });
  const [enrichment, setEnrichment] = useState<EnrichmentState>(
    directoryEnabled ? { kind: 'loading' } : { kind: 'off' },
  );
  const [selectedLeadId, setSelectedLeadId] = useState<number | null>(null);
  const [form, setForm] = useState<BookingForm>(EMPTY_BOOKING_FORM);
  // STITCH-2 SCOPE 8: the server slot grid for (selected lead, picked date).
  const [daySlots, setDaySlots] = useState<DaySlotsState>({ kind: 'idle' });
  const [pending, setPending] = useState<boolean>(false);
  const [busyFollowupId, setBusyFollowupId] = useState<number | null>(null);
  const [feedback, setFeedback] = useState<CockpitFeedback | null>(null);
  const [nowMs, setNowMs] = useState<number>(() => Date.now());

  const loadWorklist = useCallback(() => {
    let cancelled = false;
    setWorklist({ kind: 'loading' });
    setNowMs(Date.now());
    fetchMyWorkActions()
      .then((rows) => {
        if (!cancelled) setWorklist({ kind: 'ready', rows });
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          setWorklist({
            kind: 'error',
            message: e instanceof Error ? e.message : 'Failed to load your leads',
          });
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => loadWorklist(), [loadWorklist]);

  // Enrichment is a soft, best-effort read: a failure NEVER fails the cockpit -
  // it degrades to the honest "not loaded" state while the worklist still works.
  useEffect(() => {
    if (!directoryEnabled) {
      setEnrichment({ kind: 'off' });
      return;
    }
    let cancelled = false;
    setEnrichment({ kind: 'loading' });
    fetchLeadsWorkspace(null)
      .then((res) => {
        if (!cancelled) setEnrichment({ kind: 'ready', records: res.records });
      })
      .catch(() => {
        if (!cancelled) setEnrichment({ kind: 'error' });
      });
    return () => {
      cancelled = true;
    };
  }, [directoryEnabled]);

  const onSelectLead = useCallback((leadId: number) => {
    setSelectedLeadId(leadId);
    setForm(EMPTY_BOOKING_FORM);
    setFeedback(null);
  }, []);

  // Fetch the slot grid whenever the lead + date are both chosen. The server
  // computes the window + taken marks from the SAME booking-rules config the
  // write route enforces (one rule source, no client-side rule copy).
  useEffect(() => {
    if (selectedLeadId === null || form.date === '') {
      setDaySlots({ kind: 'idle' });
      return;
    }
    let cancelled = false;
    setDaySlots({ kind: 'loading' });
    fetchDaySlots(selectedLeadId, form.date)
      .then((data) => {
        if (!cancelled) setDaySlots({ kind: 'ready', data });
      })
      .catch(() => {
        if (!cancelled) setDaySlots({ kind: 'error' });
      });
    return () => {
      cancelled = true;
    };
  }, [selectedLeadId, form.date]);

  const onBook = useCallback(() => {
    if (selectedLeadId === null) return;
    const iso = form.slotIso !== '' ? form.slotIso : null;
    if (iso === null) {
      setFeedback({ kind: 'error', lines: ['Pick a date and a slot first.'] });
      return;
    }
    setPending(true);
    setFeedback(null);
    bookLeadAppointment(BOOK_DEPS, {
      leadId: selectedLeadId,
      iso,
      // RECORD-360-WORKSPACE-V1 (rider R-03): the formatter's own " IST"
      // suffix is the ONE zone label -- never append a second.
      whenLabel: formatTimestampIst(iso),
    })
      .then((res) => {
        setFeedback(res.feedback);
        // A successful book fills the slot -- refresh the grid so a second
        // attempt sees it taken.
        if (selectedLeadId !== null && form.date !== '') {
          fetchDaySlots(selectedLeadId, form.date)
            .then((data) => setDaySlots({ kind: 'ready', data }))
            .catch(() => {});
        }
      })
      .finally(() => setPending(false));
  }, [selectedLeadId, form.date, form.slotIso]);

  const onAdvanceStage = useCallback(
    (to: LeadStageTarget) => {
      if (selectedLeadId === null) return;
      const target = FRONT_HALF_STAGE_TARGETS.find((t) => t.to === to);
      setPending(true);
      setFeedback(null);
      moveLeadStage(STAGE_DEPS, {
        leadId: selectedLeadId,
        to,
        label: target ? target.label : to,
      })
        .then((res) => {
          setFeedback(res.feedback);
          // If the advance closed the lead's open work item(s) server-side, drop
          // that lead's cleared follow-ups so the worklist stays honest.
          if (res.ok && res.workItemClosed) {
            setWorklist((prev) =>
              prev.kind === 'ready'
                ? {
                    kind: 'ready',
                    rows: prev.rows.filter((r) => r.leadId !== selectedLeadId),
                  }
                : prev,
            );
          }
        })
        .finally(() => setPending(false));
    },
    [selectedLeadId],
  );

  const onFollowupTransition = useCallback(
    (id: number, status: FollowupTransition) => {
      setBusyFollowupId(id);
      setFeedback(null);
      transitionWorkAction(id, status)
        .then(() => {
          setWorklist((prev) =>
            prev.kind === 'ready'
              ? { kind: 'ready', rows: removeFollowupRow(prev.rows, id) }
              : prev,
          );
          setFeedback({
            kind: 'success',
            lines: [status === 'done' ? 'Follow-up marked done.' : 'Follow-up dismissed.'],
          });
        })
        .catch((err: unknown) => {
          setFeedback({
            kind: 'error',
            lines: [
              err instanceof Error && err.message
                ? err.message
                : 'Could not update the follow-up. Please try again.',
            ],
          });
        })
        .finally(() => setBusyFollowupId((b) => (b === id ? null : b)));
    },
    [],
  );

  return (
    <LeadCockpitContent
      worklist={worklist}
      enrichment={enrichment}
      selectedLeadId={selectedLeadId}
      bookingEnabled={bookingEnabled}
      directoryEnabled={directoryEnabled}
      callCenterReduced={callCenterReduced}
      form={form}
      slots={daySlots}
      pending={pending}
      busyFollowupId={busyFollowupId}
      feedback={feedback}
      nowMs={nowMs}
      onSelectLead={onSelectLead}
      onFormChange={setForm}
      onBook={onBook}
      onAdvanceStage={onAdvanceStage}
      onFollowupTransition={onFollowupTransition}
      onRetry={loadWorklist}
    />
  );
}
